import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
// 🆕 [Phase 56.5 — 2026-07-09] Nettoyage imports : les composants shadcn (Card, Button,
//     Dialog, Sheet, Checkbox, Calendar, Popover, Select) et les icônes lucide-react
//     ne sont plus utilisés directement — ils vivent maintenant dans les sous-composants
//     extraits (panels/, dialogs/, CalendarMonthNav).
import { toast } from 'sonner';
import { getEmployesConcernesPourJour } from '@/lib/ordreJournalier';
import { OrdonnerBTsDialog } from '@/components/crm/bon-travail/OrdonnerBTsDialog';
import { MessageHoraireDialog } from '@/components/MessageHoraireDialog';
import { OrganisationBTDialog } from '@/components/organisation/OrganisationBTDialog';
// 🆕 [Phase 56.0] Sous-composants extraits de ce fichier (refactor split)
import ServiceCallPunchDialog from '@/components/calendar/ServiceCallPunchDialog';
import JoursPluieSheet from '@/components/calendar/JoursPluieSheet';
// 🆕 [Phase 56.1] Légende footer extraite vers son propre composant
import CalendarLegende from '@/components/calendar/CalendarLegende';
// 🆕 [Phase 56.2] Bandeau d'action extrait vers son propre composant
import CalendarActionBar from '@/components/calendar/CalendarActionBar';
// 🆕 [Phase 56.5 — 2026-07-09] Mini-navigation mois extraite
import CalendarMonthNav from '@/components/calendar/CalendarMonthNav';
// 🆕 [Phase 56.3] 4 Dialogs autonomes extraits
import DialogDecalage from '@/components/calendar/dialogs/DialogDecalage';
import DialogSegmentChoice from '@/components/calendar/dialogs/DialogSegmentChoice';
import DialogConflitChoice from '@/components/calendar/dialogs/DialogConflitChoice';
import DialogRdvVisiteEffectuee from '@/components/calendar/dialogs/DialogRdvVisiteEffectuee';
// 🆕 [Phase 56.5 — 2026-07-09] Nouveaux dialogs/panels extraits
import PluieRallongementDialog from '@/components/calendar/dialogs/PluieRallongementDialog';
import EquipementsDatesDialog from '@/components/calendar/dialogs/EquipementsDatesDialog';
import EditDatesDialog from '@/components/calendar/dialogs/EditDatesDialog';
import InsertBTDialog from '@/components/calendar/dialogs/InsertBTDialog';
import AffecterEmployeDialog from '@/components/calendar/dialogs/AffecterEmployeDialog';
import CongeRapideDialog from '@/components/calendar/dialogs/CongeRapideDialog';
import DiagnosticSansJobDialog from '@/components/calendar/dialogs/DiagnosticSansJobDialog';
import SansJobPanel from '@/components/calendar/panels/SansJobPanel';
import BookingAlertsPanel from '@/components/calendar/panels/BookingAlertsPanel';
import RouteLavagePanel from '@/components/calendar/panels/RouteLavagePanel';
// 🆕 [Phase 56.4a] Context pour partager le state du calendrier avec les sous-composants
import { CalendarProvider } from '@/components/calendar/CalendarContext';
// 🆕 [Phase 56.4b] Rangée semaine extraite — consomme le CalendarContext
import CalendarWeekRow from '@/components/calendar/CalendarWeekRow';
// 🚀 [Phase v13.50 — 2026-07-14] Enrichissement BT avec shadows planification approx (Sujet 4.D)
import { enrichirBtsAvecApprox } from '@/lib/enrichirBtsApprox';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const JOURS_HEADER = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

// 🆕 [Phase 56.5 — 2026-07-09] `frSunday`, `isJourFerieCCQ`, `isCongeCCQ`,
//     `getCCQInfo`, `formatLocalTime`, `formatNacelleDisplay` étaient utilisés
//     uniquement par les blocs JSX extraits (EditDatesDialog, InsertBTDialog,
//     CalendarWeekRow). Retirés des imports du parent — chaque sous-composant
//     ré-importe ce dont il a besoin localement.


// Nettoyer un bon avant envoi PUT — retirer les champs calculés du frontend
const cleanBonForPut = (bon) => {
  const { daysInWeek, firstIdx, lastIdx, color, chefColor, isServiceCall, client_nom_display, chef_equipe_nom_display, moveMode, ...clean } = bon;
  return clean;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

// WMO Weather codes to emoji
const weatherEmoji = (code) => {
  if (code === 0) return '☀️';
  if (code <= 3) return '⛅';
  if (code <= 48) return '🌫️';
  if (code <= 57) return '🌧️';
  if (code <= 67) return '🌧️';
  if (code <= 77) return '❄️';
  if (code <= 82) return '🌧️';
  if (code <= 86) return '❄️';
  if (code >= 95) return '⛈️';
  return '☁️';
};

export function CalendrierProduction() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bons, setBons] = useState([]);

  // 🆕 [Phase 54 D.2] Calcul des employés présents par jour, dérivé des BT
  // chargés. Format : { "YYYY-MM-DD": ["emp_id1", "emp_id2", ...] }.
  // Utilisé par <GanttGridRow> pour la détection "Sans chef" (Phase 51 BIS B).
  const presentsParJour = useMemo(() => {
    const map = {};
    (bons || []).forEach(bon => {
      (bon.employes_assignes || []).forEach(ea => {
        const empId = ea.employee_id;
        if (!empId) return;
        const dates = Array.isArray(ea.dates_assignees) && ea.dates_assignees.length > 0
          ? ea.dates_assignees
          : (bon.dates_chantier || []);
        dates.forEach(d => {
          if (!map[d]) map[d] = [];
          if (!map[d].includes(empId)) map[d].push(empId);
        });
      });
    });
    return map;
  }, [bons]);
  const [employees, setEmployees] = useState([]);
  const [routesLavage, setRoutesLavage] = useState([]);
  const [noJobWorkers, setNoJobWorkers] = useState({}); // {date_str: [{employee_id, employee_nom, chef_nom}]}
  const [showNoJobPanel, setShowNoJobPanel] = useState(false);
  // 🆕 2026-05-14 — Jours dépliés dans le panneau Sans-Job (UX progressive : on
  //   voit d'abord les jours, on clique pour révéler les employés du jour).
  const [expandedNoJobDays, setExpandedNoJobDays] = useState(() => new Set());

  // 🆕 2026-05-13 — Dialog de diagnostic « Pourquoi cet employé est-il sans job ? »
  // Permet à un propriétaire de cliquer sur 🔍 dans la tuile Sans job pour voir
  // le détail de l'analyse backend (BT trouvés, statut chef/membre, dates_assignees,
  // exclusions). Évite d'avoir à investiguer manuellement via la console.
  const [diagModal, setDiagModal] = useState({ open: false, loading: false, data: null, employee_nom: '', date: '' });

  const openDiagnostic = async (employee_id, employee_nom, dateStr) => {
    setDiagModal({ open: true, loading: true, data: null, employee_nom, date: dateStr });
    try {
      const res = await axios.get(`${API_URL}/api/calendrier/no-job-debug`, {
        params: { employee_id, date: dateStr }
      });
      setDiagModal(m => ({ ...m, loading: false, data: res.data }));
    } catch (err) {
      setDiagModal(m => ({ ...m, loading: false, data: { error: err?.response?.data?.detail || err?.message || 'Erreur' } }));
    }
  };
  // 🆕 2026-04-30 — Modale d'affectation rapide
  const [affecterDialog, setAffecterDialog] = useState(null); // {date, employee_id, employee_nom}
  const [affecterBts, setAffecterBts] = useState([]);
  const [affecterChosenBt, setAffecterChosenBt] = useState('');
  const [affecterLoading, setAffecterLoading] = useState(false);
  // 🆕 Modale "Mettre en congé rapide"
  const [congeDialog, setCongeDialog] = useState(null); // {date, employee_id, employee_nom}
  const [congeType, setCongeType] = useState('vacances');
  const [congeLoading, setCongeLoading] = useState(false);
  const [congeTypesDispo, setCongeTypesDispo] = useState(null); // null = en cours de chargement, [] = vide
  const [congeTypesLoading, setCongeTypesLoading] = useState(false);
  const [serviceCalls, setServiceCalls] = useState([]);
  const [meteo, setMeteo] = useState({});
  const [punchesCache, setPunchesCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [hoveredBon, setHoveredBon] = useState(null);
  const [showDecalage, setShowDecalage] = useState(null);
  const [showEquipementsDates, setShowEquipementsDates] = useState(null);
  const [showEditDates, setShowEditDates] = useState(null);
  const [editNewEndDate, setEditNewEndDate] = useState(null);
  const [editDateMode, setEditDateMode] = useState('fin'); // 'debut' | 'fin'
  const [dragging, setDragging] = useState(null);

  // 🆕 Phase 3.5 — Dialog intra-jour pour ordonner les BT d'un chef après drop
  const [ordreDialog, setOrdreDialog] = useState(null); // { chefId, chefNom, date }

  // 🆕 2026-05-07 — Message horaire à coller dans Messenger
  const [showMessageHoraire, setShowMessageHoraire] = useState(false);
  // 🆕 [Phase 51] Organisation des BT (par employé/jour)
  const [orgBtDialogOpen, setOrgBtDialogOpen] = useState(false);
  const [orgBtDate, setOrgBtDate] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });
  const [orgBtRefresh, setOrgBtRefresh] = useState(0);

  // Helper : check collision intra-jour après drop. Si conflit, ouvre OrdonnerBTsDialog.
  // 🆕 2026-05-14 — On envoie maintenant la liste des employés assignés au BT source
  //   pour ne déclencher l'alerte d'ordre QUE si au moins un employé est cédulé
  //   sur 2 chantiers le même jour. Avant : on prenait n'importe quelle collision
  //   "même chef + même jour", ce qui était trop bruyant (Tremblay/Stéphanie vs
  //   Savard/Jacob → pas un vrai conflit).
  // 🆕 2026-05-15 — Date-aware : pour chaque journée vérifiée, on calcule la
  //   liste des employés RÉELLEMENT présents ce jour-là (respect des
  //   `dates_assignees`, `employes_exclus`, `chefs_par_jour` et
  //   `chef_dates_assignees`). Évite les fausses alertes (ex: un employé en
  //   🚫 ce jour-là sur le BT source ne devrait pas déclencher de conflit).
  const checkIntraDayCollisionAfterDrop = async (bon, newDates) => {
    if (!bon?.chef_equipe_id || !newDates?.length) return;
    const exclusIds = new Set(
      (bon.employes_exclus || []).map(e =>
        typeof e === 'string' ? e : (e?.employee_id || e?.id)
      ).filter(Boolean)
    );
    const computePresent = (day) => {
      const ids = new Set();
      const chefDuJour = (bon.chefs_par_jour || {})[day];
      if (chefDuJour && chefDuJour !== '__none__') {
        ids.add(chefDuJour);
      } else if (bon.chef_equipe_id) {
        const cda = bon.chef_dates_assignees;
        if (!Array.isArray(cda) || cda.length === 0 || cda.includes(day)) {
          ids.add(bon.chef_equipe_id);
        }
      }
      (bon.employes_assignes || []).forEach(e => {
        if (!e?.employee_id) return;
        const da = e.dates_assignees;
        if (Array.isArray(da) && da.length > 0) {
          if (!da.includes(day)) return;
        }
        ids.add(e.employee_id);
      });
      exclusIds.forEach(id => ids.delete(id));
      return ids;
    };
    try {
      // Parcourir les nouveaux jours et repérer le 1er avec collision RÉELLE
      for (const day of newDates) {
        const presentIds = computePresent(day);
        if (presentIds.size === 0) continue;
        const empParam = [...presentIds].join(',');
        const r = await axios.get(`${API_URL}/api/crm/bons-travail/collisions`, {
          params: {
            chef_id: bon.chef_equipe_id,
            date: day,
            exclude_bt_id: bon.id,
            employee_ids: empParam,
          }
        });
        const others = r.data?.bons_travail || [];
        if (others.length >= 1) {
          setOrdreDialog({
            chefId: bon.chef_equipe_id,
            chefNom: bon.chef_equipe_nom || 'Chef',
            date: day,
          });
          return; // 1er jour conflictuel suffit
        }
      }
    } catch (e) {
      // Silencieux — ne doit pas bloquer le flow principal
      console.warn('checkIntraDayCollisionAfterDrop error:', e?.message);
    }
  };
  const [showServiceCallPunch, setShowServiceCallPunch] = useState(null);
  const [showJoursPluie, setShowJoursPluie] = useState(false);
  const [bonsPluie, setBonsPluie] = useState([]);
  // Congés approuvés par date (pour les indicateurs sur le calendrier)
  const [congesParDate, setCongesParDate] = useState({});
  const [showMeteo, setShowMeteo] = useState(false); // ✅ OFF par défaut
  const [showConges, setShowConges] = useState(false);

  const [showRoutePanel, setShowRoutePanel] = useState(false);
  const [bonsLavage, setBonsLavage] = useState([]);
  const [selectedBonsRoute, setSelectedBonsRoute] = useState(new Set());
  const [routeDate, setRouteDate] = useState('');
  const [routeEquipe, setRouteEquipe] = useState('');
  const [savingRoute, setSavingRoute] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [nacellesDisponibles, setNacellesDisponibles] = useState([]);
  const [nacelleWarning, setNacelleWarning] = useState(false);
  const [showInsertBT, setShowInsertBT] = useState(null); // { date: 'YYYY-MM-DD' }
  const [unscheduledBons, setUnscheduledBons] = useState([]);
  const [searchBT, setSearchBT] = useState('');
  const [loadingInsert, setLoadingInsert] = useState(false);
  const [agendaEvents, setAgendaEvents] = useState([]);
  const [showAgenda, setShowAgenda] = useState(false); // ✅ OFF par défaut
  const [rdvPopup, setRdvPopup] = useState(null); // Pour le popup "Visite effectuée"
  const [showSegmentChoice, setShowSegmentChoice] = useState(null); // { bon, segments, newStartDate }
  const [showConflitChoice, setShowConflitChoice] = useState(null); // { bon, conflictingBon, newDates }
  // 🆕 2026-05-09 — Booking alerts (BT cédulés ≠ recommandé) + jour-de-pluie cohésion
  const [bookingAlerts, setBookingAlerts] = useState([]);
  const [showBookingPanel, setShowBookingPanel] = useState(false);
  const [bookingActionLoading, setBookingActionLoading] = useState(null);
  const [pluieRallongementDialog, setPluieRallongementDialog] = useState(null); // {bon, date, btsARallonger}
  const navigate = useNavigate();
  const clickTimeoutRef = useRef(null);
  // 🆕 [Phase 104 — Bug #1] Guard synchrone anti double-click sur "Créer la route".
  // Le state React `savingRoute` est asynchrone et peut laisser passer 2 clicks rapides
  // avant que le bouton ne soit disabled — donc on utilise un ref synchrone en plus.
  const createRouteInFlightRef = useRef(false);

  // Gestion du clic simple vs double-clic
  // 🐛 [v9.3 — 2026-06-12] Le 2e argument peut être soit un MouseEvent (clic direct
  // depuis le DOM) soit un `cellDate` string (depuis CalendarWeekRow qui passe
  // `(bon, cellDate)`). On ne fait `stopPropagation` que si c'est bien un event.
  const handleBarClick = (bon, eventOrCellDate) => {
    if (eventOrCellDate && typeof eventOrCellDate.stopPropagation === 'function') {
      eventOrCellDate.stopPropagation();
    }
    if (clickTimeoutRef.current) {
      // Double-clic détecté - annuler le timeout et ouvrir le dialogue de dates
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
      openEditDatesDialog(bon);
    } else {
      // Premier clic - attendre pour voir si c'est un double-clic
      clickTimeoutRef.current = setTimeout(() => {
        clickTimeoutRef.current = null;
        // Simple clic - naviguer vers le bon de travail
        navigate(`/crm/projets/${bon.dossier_id}?tab=bon-travail&bonId=${bon.id}`);
      }, 250); // 250ms pour détecter un double-clic
    }
  };

  // ✅ Détecter les segments de dates non-consécutives
  const detectSegments = (dates) => {
    if (!dates || dates.length === 0) return [];
    const sorted = [...dates].sort();
    const segments = [];
    let currentSegment = [sorted[0]];
    
    for (let i = 1; i < sorted.length; i++) {
      const prevDate = new Date(sorted[i - 1] + 'T12:00:00');
      const currDate = new Date(sorted[i] + 'T12:00:00');
      const diffDays = Math.round((currDate - prevDate) / 86400000);
      
      // Si l'écart est > 3 jours (weekend + 1 jour), c'est un nouveau segment
      if (diffDays > 3) {
        segments.push(currentSegment);
        currentSegment = [sorted[i]];
      } else {
        currentSegment.push(sorted[i]);
      }
    }
    segments.push(currentSegment);
    return segments;
  };

  useEffect(() => { fetchData(); }, [currentDate]);
  // 🐛 [Phase 56.5 — 2026-07-09] Bug latent legacy corrigé : DialogRdvVisiteEffectuee
  //     onConfirm appelait `loadData()` qui n'était jamais défini (crash au clic
  //     "Visite effectuée"). Alias vers fetchData pour restaurer le comportement
  //     attendu (rafraîchir toutes les données). Aucune modification de la
  //     signature ou du contenu de fetchData — juste un alias.
  const loadData = () => fetchData();

  // 🚀 Hotfix 2026-05-03 — Phase 3.5 perf fix : précalcul global des chefs/employés
  // avec BT non ordonnés, regroupés par date.
  // 🆕 [Phase 19B] 2026-05-31 — Itère sur TOUS les employés concernés par chaque
  //   jour (chef principal, chef-du-jour via chefs_par_jour, membres via
  //   employes_assignes filtrés par dates_assignees), pas uniquement chef_equipe_id.
  //   Fix Bug B2 : Grégory (chef-du-jour) recevait pas de marquage alors que
  //   Gabrielle (membre) en avait un.
  //   Le critère "à ordonner" = un employé a >=2 items le même jour avec au moins
  //   un sans ordre journalier effectif pour CE jour-là.
  const unorderedByDate = useMemo(() => {
    const byDate = {}; // { dateStr: { empId: [bons] } }
    bons.forEach((b) => {
      (b.dates_chantier || []).forEach((d) => {
        const empIds = getEmployesConcernesPourJour(b, d);
        if (empIds.size === 0) return;
        if (!byDate[d]) byDate[d] = {};
        empIds.forEach((eid) => {
          (byDate[d][eid] ||= []).push(b);
        });
      });
    });
    const out = {};
    Object.entries(byDate).forEach(([dateStr, emps]) => {
      const unordered = [];
      Object.entries(emps).forEach(([empId, bts]) => {
        if (bts.length < 2) return;
        // Au moins 1 BT sans ordre effectif pour ce jour précis
        const hasMissing = bts.some((b) => {
          const opj = b.ordres_par_jour || {};
          if (opj[dateStr] != null) return false;
          return b.ordre_journalier == null;
        });
        if (hasMissing) {
          // Nom : on essaie d'abord l'employé dans la liste globale (employees)
          const emp = (employees || []).find(e => e.id === empId);
          unordered.push({
            chefId: empId,
            chefNom: emp ? `${emp.prenom || ''} ${emp.nom || ''}`.trim() : (bts[0].chef_equipe_nom || 'Employé'),
            count: bts.length,
          });
        }
      });
      if (unordered.length > 0) out[dateStr] = unordered;
    });
    return out;
  }, [bons, employees]);

  // Auto-charge les punches de tous les bons actifs dès leur chargement
  useEffect(() => {
    if (bons.length === 0) return;
    const activeBonIds = bons.filter(b => b.statut !== 'termine').map(b => b.id);
    activeBonIds.forEach(id => fetchPunches(id));
  }, [bons]);

  // Cleanup du timeout si le composant se démonte
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDayDate = new Date(year, month + 1, 0);
      const lastDay = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDayDate.getDate()).padStart(2, '0')}`;

      const [bRes, eRes, mRes, rRes, scRes, cRes, agRes, njRes, baRes, dRes, biRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/bons-travail`),
        axios.get(`${API_URL}/api/employees`),
        axios.get(`${API_URL}/api/meteo`),
        axios.get(`${API_URL}/api/crm/routes-lavage`),
        axios.get(`${API_URL}/api/crm/service-calls`),
        axios.get(`${API_URL}/api/conges/employes-en-conge?date_debut=${firstDay}&date_fin=${lastDay}`),
        axios.get(`${API_URL}/api/agenda-perso/all-employees?year=${year}&month=${month + 1}`),
        axios.get(`${API_URL}/api/calendrier/no-job-workers?annee=${year}&mois=${month + 1}`).catch(() => ({ data: { no_job_days: {}, total_alerts: 0 } })),
        // 🆕 2026-05-09 — Booking alerts (tous les BT à venir, pas filtré par mois)
        axios.get(`${API_URL}/api/calendrier/booking-alerts`).catch(() => ({ data: { alerts: [], total: 0 } })),
        // 🚀 Hotfix 2026-05-03 — Parallélisé (avant : séquentiel après Promise.all, +300-800ms)
        axios.get(`${API_URL}/api/crm/dossiers?limit=2000`).catch(() => ({ status: 0, data: { dossiers: [] } })),
        // 🎨 [Phase v13.53 — 2026-07-21] BT internes réactivés dans le calendrier.
        // Contexte historique : le commentaire précédent affirmait « /api/bt-internes
        // supprimé Phase 33 — migration terminée vers BT sous client interne PRQC »
        // et remplaçait l'appel réel par `Promise.resolve({...bons_internes: []})`.
        // Vérifié 2026-07-21 : l'endpoint `/api/bt-internes` (backend/routers/bt_internes.py)
        // existe TOUJOURS. Résultat du bug : les BT internes n'étaient plus jamais
        // chargés dans le calendrier depuis Phase 33. `/api/crm/bons-travail` (L400)
        // exclut explicitement `est_interne=True` quand appelé sans `dossier_id`.
        // Voir RCA `/app/memory/rca_v13_53_calendrier_couleurs_bt_internes.md`.
        // Aucun filtre backend par statut employé (first_login, invitation, etc.) —
        // les BT internes assignés à un employé jamais loggé apparaissent quand même.
        axios.get(`${API_URL}/api/bt-internes`).catch(() => ({ status: 0, data: { bons_internes: [] } })),
      ]);
      if (bRes.status === 200) {
        const d = bRes.data;
        let bonsList = d.bons_travail || [];
        // 🆕 2026-05-23 — Fusion BT internes : on les ajoute à la liste principale
        // avec un flag `est_interne: true` pour le rendu distinct. Ils sont
        // affichés sur les dates de `dates_chantier`.
        if (biRes.status === 200) {
          const internes = (biRes.data?.bons_internes || []).map(bi => ({
            ...bi,
            est_interne: true,
          }));
          bonsList = [...bonsList, ...internes];
        }
        // Charger les noms de clients pour tous les dossiers des BT (cache depuis dRes parallélisé)
        const dossierIds = [...new Set(bonsList.map(b => b.dossier_id).filter(Boolean))];
        const newCache = {};
        if (dossierIds.length > 0 && dRes.status === 200) {
          const dData = dRes.data;
          (dData.dossiers || []).forEach(d => { newCache[d.id] = d; });
        }
        setDossierCache(prev => ({ ...prev, ...newCache }));
        setBons(bonsList);
      }
      let terrainIds = new Set();
      if (eRes.status === 200) { const d = eRes.data; setEmployees(d.employees || []); terrainIds = new Set((d.employees || []).filter(e => e.type_employe === 'terrain').map(e => e.id)); }
      if (rRes.status === 200) { const d = rRes.data; setRoutesLavage(d.routes || []); }
      if (scRes.status === 200) { const d = scRes.data; setServiceCalls(d.service_calls || []); }
      if (mRes.status === 200) {
        const d = mRes.data;
        const map = {};
        (d.forecasts || []).forEach(f => { map[f.date] = f; });
        setMeteo(map);
      }
      if (cRes.status === 200) {
        const congesList = cRes.data;
        const map = {};
        for (const c of congesList.filter(c => terrainIds.has(c.employee_id))) {
          const start = new Date(c.date_debut + 'T12:00:00');
          const end = new Date(c.date_fin + 'T12:00:00');
          let cur = new Date(start);
          while (cur <= end) {
            const ds = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}-${String(cur.getDate()).padStart(2,'0')}`;
            if (!map[ds]) map[ds] = [];
            map[ds].push({ id: c.employee_id, nom: c.employe_nom || c.employee_nom, type: c.type_conge });
            cur.setDate(cur.getDate() + 1);
          }
        }
        setCongesParDate(map);
      }
      if (agRes.status === 200) {
        const agData = agRes.data;
        setAgendaEvents(agData.events || []);
      }
      // No-job workers
      setNoJobWorkers(njRes.data?.no_job_days || {});
      // 🆕 2026-05-09 — Stocker les booking alerts dans le state
      setBookingAlerts(baRes.data?.alerts || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // 🆕 2026-04-30 — Affecter rapidement un employé non cédulé à un BT
  const openAffecterDialog = async (date, employee_id, employee_nom) => {
    setAffecterDialog({ date, employee_id, employee_nom });
    setAffecterChosenBt('');
    try {
      const r = await axios.get(`${API_URL}/api/calendrier/bts-du-jour?date=${date}`);
      setAffecterBts(r.data?.bts || []);
    } catch (e) {
      setAffecterBts([]);
    }
  };

  // 🆕 2026-05-09 — Booking alerts : ajuster la durée d'un BT (+/- jours)
  // Étend ou raccourcit `dates_chantier` en ajoutant/retirant des jours ouvrables
  // à la fin, puis appelle PUT (qui auto-shifte equipements_nacelle_dates).
  const applyBookingAdjustment = async (alert, deltaDays) => {
    setBookingActionLoading(alert.bon_id);
    try {
      // Récupérer le BT à jour
      const fullBon = bons.find(b => b.id === alert.bon_id);
      if (!fullBon) { toast.error('BT introuvable'); return; }
      const dates = [...(fullBon.dates_chantier || [])].sort();
      let newDates = [...dates];
      if (deltaDays > 0) {
        // Ajouter `deltaDays` jours ouvrables après la dernière date
        let cur = dates.length > 0 ? new Date(dates[dates.length - 1] + 'T12:00:00') : new Date();
        for (let i = 0; i < deltaDays; i++) {
          do {
            cur.setDate(cur.getDate() + 1);
          } while (cur.getDay() === 0 || cur.getDay() === 6);
          newDates.push(cur.toISOString().split('T')[0]);
        }
      } else if (deltaDays < 0) {
        // Retirer les |deltaDays| dernières dates
        newDates = newDates.slice(0, Math.max(1, newDates.length + deltaDays));
      } else {
        return;
      }
      newDates.sort();
      await axios.put(`${API_URL}/api/crm/bons-travail/${alert.bon_id}`, {
        ...cleanBonForPut(fullBon),
        dates_chantier: newDates,
      });
      const action = deltaDays > 0 ? `+${deltaDays} jour(s)` : `${deltaDays} jour(s)`;
      toast.success(`BT mis à jour (${action})`);
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur lors de l\'ajustement';
      toast.error(msg);
    } finally {
      setBookingActionLoading(null);
    }
  };

  // 🆕 2026-05-09 — Confirmer l'insertion d'un jour-de-pluie avec ou sans rallongement
  const confirmPluieInsertion = (rallonger) => {
    if (!pluieRallongementDialog) return;
    pluieRallongementDialog.retry(rallonger);
    setPluieRallongementDialog(null);
  };

  const submitAffecter = async () => {
    if (!affecterDialog || !affecterChosenBt) return;
    setAffecterLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/calendrier/affecter-employe-bt`, {
        employee_id: affecterDialog.employee_id,
        bt_id: affecterChosenBt,
        date: affecterDialog.date,
      });
      if (res.status === 200) {
        toast.success(`${affecterDialog.employee_nom} affecté(e) au BT pour le ${affecterDialog.date}`);
        setAffecterDialog(null);
        fetchData(); // Rafraîchir tout (la liste no-job sera mise à jour)
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'affectation");
    } finally {
      setAffecterLoading(false);
    }
  };

  // 🆕 2026-04-30 — Mettre en congé rapide
  const openCongeDialog = async (date, employee_id, employee_nom) => {
    setCongeDialog({ date, employee_id, employee_nom });
    setCongeType('');
    setCongeTypesDispo(null);
    setCongeTypesLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/calendrier/types-conges-disponibles/${employee_id}`);
      const types = r.data?.types || [];
      setCongeTypesDispo(types);
      // Sélectionner par défaut le 1er type available
      const firstAvail = types.find(t => t.available);
      if (firstAvail) setCongeType(firstAvail.key);
    } catch (e) {
      setCongeTypesDispo([]);
      toast.error("Impossible de charger les types de congés");
    } finally {
      setCongeTypesLoading(false);
    }
  };

  const submitConge = async () => {
    if (!congeDialog || !congeType) return;
    setCongeLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/calendrier/mettre-en-conge-rapide`, {
        employee_id: congeDialog.employee_id,
        date: congeDialog.date,
        type_conge: congeType,
      });
      if (res.status === 200) {
        toast.success(`${congeDialog.employee_nom} mis(e) en congé pour le ${congeDialog.date}`);
        setCongeDialog(null);
        setCongeType('vacances');
        fetchData();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création du congé');
    } finally {
      setCongeLoading(false);
    }
  };

  const fetchBonsPluie = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/bons-travail-pluie`);
      if (res.status === 200) { const d = res.data; setBonsPluie(d.bons_travail || []); }
    } catch (e) { console.error(e); }
  };

  // Double-clic sur une case vide → ouvrir le dialogue d'insertion de BT
  const handleDayDoubleClick = async (dateStr) => {
    setShowInsertBT({ date: dateStr });
    setSearchBT('');
    setLoadingInsert(true);
    try {
      // Charger tous les BT sans dates_chantier (non cédulés)
      const res = await axios.get(`${API_URL}/api/crm/bons-travail`);
      if (res.status === 200) {
        const data = res.data;
        const all = data.bons_travail || [];
        // Filtrer: pas de dates OU dates vides, et pas annulés/terminés
        const unscheduled = all.filter(b =>
          (!b.dates_chantier || b.dates_chantier.length === 0) &&
          b.statut !== 'annule' && b.statut !== 'termine'
        );
        // Enrichir avec info dossier
        const dossierIds = [...new Set(unscheduled.map(b => b.dossier_id).filter(Boolean))];
        const dossierMap = {};
        for (const did of dossierIds) {
          try {
            const dRes = await axios.get(`${API_URL}/api/crm/dossiers/${did}`);
            if (dRes.status === 200) dossierMap[did] = dRes.data;
          } catch (e) { console.error("Error:", e); }
        }
        setUnscheduledBons(unscheduled.map(b => ({
          ...b,
          client_nom: dossierMap[b.dossier_id]?.client_nom || b.client_nom || '',
          nom_dossier: dossierMap[b.dossier_id]?.nom_dossier || '',
          departement: dossierMap[b.dossier_id]?.departement || '',
        })));
      }
    } catch (e) { console.error(e); }
    finally { setLoadingInsert(false); }
  };

  const handleInsertBT = async (bon, startDate) => {
    // Calculer les dates de chantier à partir de startDate
    // Utiliser le nombre d'heures budgétées pour estimer le nombre de jours
    const heures = bon.heures_budgetees || 40;
    const heuresParJour = 7.5;
    const nbJours = Math.max(1, Math.ceil(heures / heuresParJour));
    const newDates = [];
    let current = new Date(startDate + 'T12:00:00');
    let count = 0;
    while (count < nbJours) {
      if (current.getDay() >= 1 && current.getDay() <= 5) {
        newDates.push(current.toISOString().split('T')[0]);
        count++;
      }
      current.setDate(current.getDate() + 1);
    }
    try {
      await axios.put(`${API_URL}/api/crm/bons-travail/${bon.id}`, 
        { ...cleanBonForPut(bon), dates_chantier: newDates },
        { headers: getAuthHeaders() }
      );
      toast.success(`${bon.client_nom || 'BT'} cedule a partir du ${startDate} (${newDates.length} jours)`);
      setShowInsertBT(null);
      fetchData();
    } catch (e) { toast.error('Erreur'); }
  };


  const fetchBonsLavage = async () => {
    try {
      const [alertesRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/alertes-lavage`),
        axios.get(`${API_URL}/api/employees`),
      ]);
      if (alertesRes.status === 200) {
        const alertes = alertesRes.data?.alertes || [];
        const btRes = await axios.get(`${API_URL}/api/crm/bons-travail`);
        const allBts = btRes.data?.bons_travail || [];
        const btMap = {};
        for (const b of allBts) btMap[b.id] = b;

        const bons = alertes.map(a => ({
          id: a.bon_id,
          dossier_id: a.dossier_id,
          client_nom: a.client_nom,
          adresse_chantier: a.adresse,
          ville_chantier: '',
          notes_lavage_pression: btMap[a.bon_id]?.notes_lavage_pression || null,
          nacelle_lavage_requise: btMap[a.bon_id]?.nacelle_lavage_requise || false,
          premier_jour_chantier: a.premier_jour_chantier,
          jours_restants: a.jours_restants,
        }));
        setBonsLavage(bons);
      }
      if (empRes.status === 200) {
        setAllEmployees(empRes.data?.employees || []);
      }
    } catch (e) { console.error('Erreur fetchBonsLavage:', e); }
  };

  const handleCreateRoute = async () => {
    if (selectedBonsRoute.size === 0 || !routeDate) {
      toast.error('Sélectionnez au moins un bon et une date');
      return;
    }
    // 🆕 [Phase 104 — Bug #1] Guard synchrone anti double-click.
    // `setSavingRoute(true)` étant async, un double-click ultra-rapide peut passer.
    if (createRouteInFlightRef.current) return;
    createRouteInFlightRef.current = true;
    setSavingRoute(true);
    try {
      const bonsIds = Array.from(selectedBonsRoute);
      const bonsData = bonsLavage.filter(b => bonsIds.includes(b.id));

      // Formater les employés depuis routeEquipe (IDs séparés par virgule)
      const empIds = (routeEquipe || '').split(',').filter(Boolean);
      const employes = allEmployees
        .filter(e => empIds.includes(e.id))
        .map(e => ({ id: e.id, nom: `${e.prenom} ${e.nom}` }));

      // ✅ Payload conforme au modèle RouteLavage
      const payload = {
        date_route: routeDate,
        employes,
        clients: bonsData.map((b, i) => ({
          dossier_id: b.dossier_id || '',
          client_nom: b.client_nom || b.titre || '—',
          adresse: b.adresse_chantier || '',
          ville: b.ville_chantier || '',
          ordre: i,
          statut: 'en_attente',
          notes_lavage_pression: b.notes_lavage_pression || null,
          nacelle_lavage_requise: b.nacelle_lavage_requise || false,
        })),
      };

      // Nacelle : ajouter note automatique si nacelle disponible
      const clientsNacelle = bonsData.filter(b => b.nacelle_lavage_requise);
      if (clientsNacelle.length > 0 && nacellesDisponibles.length > 0) {
        const nacelle = nacellesDisponibles[0];
        payload.nacelle_assignee_id = nacelle.id;
        payload.nacelle_nom = nacelle.nom;
        const clientsNoms = clientsNacelle.map(b => {
          const retour = b.nacelle_retour === 'client' ? '→ Laisser nacelle sur place' : '→ Ramener nacelle au bureau';
          return `${b.client_nom} (${retour})`;
        }).join(', ');
        payload.notes = (payload.notes || '') + `\n\u{1F3D7}\uFE0F Nacelle requise — Prendre ${nacelle.nom} (${clientsNoms})`.trim();
      } else if (clientsNacelle.length > 0 && nacellesDisponibles.length === 0) {
        payload.notes = (payload.notes || '') + `\n⚠ ATTENTION : Nacelle requise pour ${clientsNacelle.map(b => b.client_nom).join(', ')} mais AUCUNE nacelle disponible ce jour-là !`;
      }

      // Échafauds : ajouter note automatique
      const clientsEchafauds = bonsData.filter(b => b.echafauds_a_monter);
      if (clientsEchafauds.length > 0) {
        const echNotes = clientsEchafauds.map(b => {
          const retour = b.echafauds_remorque_retour === 'client' ? '→ Laisser remorque sur place' : '→ Rapporter remorque au bureau';
          return `${b.client_nom} (${retour})`;
        }).join(', ');
        payload.notes = (payload.notes || '') + `\n🔧 Échafauds — ${echNotes}`.trim();
      }

      // ✅ Bon endpoint
      const res = await axios.post(`${API_URL}/api/crm/routes-lavage`, payload);
      if (res.status === 200) {
        toast.success('Route de lavage créée !');
        setShowRoutePanel(false);
        setSelectedBonsRoute(new Set());
        setRouteDate('');
        setRouteEquipe('');
      } else {
        const d = res.data;
        toast.error(d.detail || 'Erreur création route');
      }
    } catch (e) { 
      const d = e.response?.data;
      toast.error(d?.detail || 'Erreur réseau'); 
    }
    finally {
      setSavingRoute(false);
      createRouteInFlightRef.current = false;
    }
  };



  // Get weeks (Sun-Sat) for current month
  const getWeeks = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Find first Sunday on or before the 1st
    let start = new Date(firstDay);
    while (start.getDay() !== 0) start.setDate(start.getDate() - 1);

    const weeks = [];
    let current = new Date(start);
    while (current <= lastDay || current.getDay() !== 0) {
      const week = [];
      for (let i = 0; i < 7; i++) { // Sun-Sat
        week.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
      if (current > lastDay && current.getDay() === 1) break;
    }
    return weeks;
  };

  const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Get bons that have dates in a given week — split into segments if gaps
  const getBonsForWeek = (weekDates) => {
    const weekDateStrs = weekDates.map(toDateStr);
    const items = [];
    
    // Regular bons de travail
    bons.filter(bon => {
      const bonDates = bon.dates_chantier || [];
      return bonDates.some(d => weekDateStrs.includes(d));
    }).forEach(bon => {
      const bonDates = bon.dates_chantier || [];
      const daysInWeek = weekDateStrs.map(d => bonDates.includes(d));
      const emp = employees.find(e => e.id === bon.chef_equipe_id);
      // 🎨 [Phase v13.53 — 2026-07-21] BT sans chef d'équipe → GRIS #94a3b8
      //   (uniformisé avec le rendu cellule-par-cellule de resolveCellColor).
      //   AVANT v13.53 : mauve #A855F7, incohérent avec le gris utilisé quand
      //   un chef était assigné mais pas cédulé ce jour. Voir RCA
      //   `/app/memory/rca_v13_53_calendrier_couleurs_bt_internes.md`.
      // 🆕 2026-05-14 (v2) — On considère AUSSI "solo" un BT dont le "chef" assigné
      //   n'est en réalité PAS un chef d'équipe (ex. Stéphanie = peintre seul sur
      //   un BT à son nom). Sinon le BT prend la couleur perso du peintre, ce qui
      //   prête à confusion dans le calendrier.
      // 🔧 2026-05-23 — Exception ex-chef : un ex-chef rétrogradé (peintre maintenant)
      //   mais avec une `couleur_equipe` historique conserve sa couleur sur ses BTs
      //   passés. Ainsi les BTs d'Audrey restent bleus même après sa rétrogradation,
      //   et les nouveaux BTs de Gabrielle (qui hérite de la même couleur) restent
      //   bleus aussi → cohérence visuelle.
      const empIsRealChef = !!emp && (emp.role_terrain === 'chef_equipe' || emp.type_employe === 'chef_equipe');
      const empIsExChefWithColor = !!emp && !empIsRealChef && !!emp.couleur_equipe;
      const noChef = !bon.chef_equipe_id || (!!emp && !empIsRealChef && !empIsExChefWithColor);
      // 🆕 2026-05-23 — BT internes ont une couleur dédiée (gris-lavande) pour
      // les distinguer immédiatement des BT clients dans le calendrier.
      // 🎨 [v13.53] Exception : BT interne SANS chef → gris #94a3b8 uniforme
      // (règle « sans chef → gris » prime sur la convention « interne = #64748B »).
      const isBtInterne = !!bon.est_interne;
      const defaultColor = noChef
        ? '#94a3b8'                                   // Sans chef → gris uniforme
        : (isBtInterne
            ? '#64748B'                               // BT interne AVEC chef → slate-500
            : (emp?.couleur_equipe || '#3B82F6'));    // BT client AVEC chef → couleur chef

      // 🚀 [Phase v13.46.6 — 2026-07-18] `chefs_par_jour` est désormais lu
      // DIRECTEMENT au rendu via `resolveCellColor(segBt, cellDate, presentsParJour)`
      // dans `GanttGridCalendrier` — plus besoin de résoudre le chef du jour ici
      // pour segmenter. Le helper `resolveChefDuJour` local a été retiré (dead
      // code depuis la refonte cellule-par-cellule).
      const _chefsParJour = bon.chefs_par_jour || {};   // conservé pour lecture éventuelle
      void _chefsParJour;

      // 🚀 [Phase v13.46.6 — 2026-07-18] Split UNIQUEMENT par consécutivité de jours.
      // Avant v13.46.6, on cassait aussi le segment quand `chefs_par_jour[date]`
      // changeait → 2+ items superposés sur les mêmes `dates_chantier`, un item
      // par chef. Un item cachait visuellement l'autre → utilisatrice ne voyait
      // qu'UNE couleur (chef principal), sans variations par jour.
      //
      // Nouveau : 1 item par run consécutif de jours. La variation de couleur
      // par chef effectif est peinte CELLULE PAR CELLULE côté rendu
      // (`GanttGridCalendrier` — nouveau fond multi-couleurs via `resolveCellColor`).
      // Une seule bande continue avec cellules aux couleurs des chefs effectifs.
      let segStart = -1;
      const flushSegment = (endIdx) => {
        if (segStart !== -1) {
          items.push({
            ...bon,
            daysInWeek,
            firstIdx: segStart,
            lastIdx: endIdx,
            color: defaultColor,          // couleur de base — surchargée cellule par cellule
            chefColor: defaultColor,
            // Legacy Phase 34 conservé pour tooltip/nom d'équipe global de la bande
            chef_equipe_nom: bon.chef_equipe_nom,
            chef_du_segment_id: bon.chef_equipe_id,
            isPassationDepuis: null,      // 🚀 v13.46.6 : passation visuelle
                                          // implicite via le changement de
                                          // couleur des cellules (plus besoin
                                          // de bordure pointillée séparée).
          });
        }
        segStart = -1;
      };
      for (let i = 0; i <= 7; i++) {
        if (i < 7 && daysInWeek[i]) {
          if (segStart === -1) {
            segStart = i;
          }
        } else {
          flushSegment(i - 1);
        }
      }
    });
    
    // Service calls (yellow)
    // 🆕 [Phase 82 — 2026-06-22] Ajout `dates_chantier: [sc.date_prevue]` pour
    // que GanttGridCalendrier puisse calculer le segment via computeBTRuns().
    // Sans ça, le SC reste invisible (régression introduite par v9 — 2026-06-12 Bug #1 P0).
    serviceCalls.filter(sc => {
      return sc.date_prevue && weekDateStrs.includes(sc.date_prevue);
    }).forEach(sc => {
      const daysInWeek = weekDateStrs.map(d => d === sc.date_prevue);
      const firstIdx = daysInWeek.indexOf(true);
      const lastIdx = firstIdx;
      items.push({
        ...sc,
        daysInWeek,
        firstIdx,
        lastIdx,
        color: '#EAB308',
        chefColor: '#EAB308',
        isServiceCall: true,
        dates_chantier: [sc.date_prevue], // 🆕 [Phase 82] Format BT-like attendu par GanttGridCalendrier
        client_nom_display: sc.client_nom,
        chef_equipe_nom_display: sc.chef_equipe_nom,
      });
    });
    
    // Tri stable par chef_equipe_id pour que chaque equipe garde la meme position verticale
    // Grouper par chef_equipe_id pour que chaque équipe ait sa propre "lane"
    const laneOrder = {};
    let laneIdx = 0;
    // D'abord assigner les lanes basées sur tous les bons (pas juste cette semaine)
    const allChefIds = [...new Set(bons.map(b => b.chef_equipe_id || '').filter(Boolean))].sort();
    allChefIds.forEach(cid => { laneOrder[cid] = laneIdx++; });
    // Les bons sans chef à la fin
    laneOrder[''] = laneIdx;

    items.sort((a, b) => {
      const laneA = laneOrder[a.chef_equipe_id || ''] ?? 999;
      const laneB = laneOrder[b.chef_equipe_id || ''] ?? 999;
      if (laneA !== laneB) return laneA - laneB;
      // Même équipe: trier par date de début dans la semaine
      return (a.firstIdx || 0) - (b.firstIdx || 0);
    });
    
    return items;
  };

  // Fetch dossier info for tooltip
  const [dossierCache, setDossierCache] = useState({});
  const fetchDossierInfo = async (dossierId) => {
    if (dossierCache[dossierId]) return;
    try {
      const res = await axios.get(`${API_URL}/api/crm/dossiers/${dossierId}`);
      if (res.status === 200) {
        const data = res.data;
        setDossierCache(prev => ({ ...prev, [dossierId]: data }));
      }
    } catch (e) { /* ignore */ }
  };

  const fetchPunches = async (bonId) => {
    if (punchesCache[bonId]) return;
    try {
      const res = await axios.get(`${API_URL}/api/punch/bon-travail/${bonId}`);
      if (res.status === 200) {
        const data = res.data;
        setPunchesCache(prev => ({ ...prev, [bonId]: data.punches || [] }));
      }
    } catch (e) { /* ignore */ }
  };

  // Ouvrir le dialogue d'édition — double-clic = changer la date de DÉBUT
  const openEditDatesDialog = (bon) => {
    const sortedDates = [...(bon.dates_chantier || [])].sort();
    const segments = detectSegments(sortedDates);
    
    // ✅ Si plusieurs segments, demander lequel déplacer
    if (segments.length > 1) {
      setShowSegmentChoice({ bon, segments });
      setHoveredBon(null);
    } else {
      // Un seul segment - ouvrir directement le dialogue de date
      const firstDate = sortedDates[0];
      setEditNewEndDate(firstDate ? new Date(firstDate + 'T12:00:00') : new Date());
      setShowEditDates({ bon });
      setHoveredBon(null);
    }
  };

  // Confirmer: déplacer tout le bloc en gardant la même durée
  const confirmEditDates = () => {
    if (!showEditDates || !editNewEndDate) return;
    const bon = showEditDates.bon;
    const segment = showEditDates.segment; // Peut être undefined si on déplace tout
    const newStartDate = editNewEndDate.toISOString().split('T')[0];
    setShowEditDates(null);
    
    // ✅ Si segment spécifique, déplacer seulement ce segment
    if (segment && segment.length > 0) {
      handleMoveSegment(bon, segment, newStartDate);
    } else {
      // Déplacer tout le bloc
      handleMoveBlock(bon, newStartDate);
    }
  };

  // ✅ Déplacer un segment spécifique d'un BT avec dates non-consécutives
  const handleMoveSegment = async (bon, segmentDates, newStartDate) => {
    const oldDates = [...(bon.dates_chantier || [])].sort();
    const nbJoursSegment = segmentDates.length;
    const oldSegmentStart = segmentDates[0];
    
    // Générer les nouvelles dates pour ce segment uniquement
    const newSegmentDates = [];
    let current = new Date(newStartDate + 'T12:00:00');
    while (newSegmentDates.length < nbJoursSegment) {
      if (current.getDay() >= 1 && current.getDay() <= 5) {
        newSegmentDates.push(current.toISOString().split('T')[0]);
      }
      current.setDate(current.getDate() + 1);
    }
    
    // Remplacer les anciennes dates du segment par les nouvelles
    const finalDates = oldDates.filter(d => !segmentDates.includes(d)).concat(newSegmentDates).sort();
    
    // Décaler les équipements de ce segment
    const dayShift = Math.round((new Date(newStartDate + 'T12:00:00') - new Date(oldSegmentStart + 'T12:00:00')) / 86400000);
    const updatedEquipements = (bon.equipements || []).map(eq => ({
      ...eq,
      dates_utilisation: (eq.dates_utilisation || []).map(d => {
        if (segmentDates.includes(d)) {
          const nd = new Date(new Date(d + 'T12:00:00').getTime() + dayShift * 86400000);
          while (nd.getDay() === 0 || nd.getDay() === 6) nd.setDate(nd.getDate() + (dayShift >= 0 ? 1 : -1));
          return nd.toISOString().split('T')[0];
        }
        return d; // Garder les équipements des autres segments inchangés
      })
    }));
    
    // Sauvegarder
    try {
      const res = await axios.put(`${API_URL}/api/crm/bons-travail/${bon.id}`,
        { ...cleanBonForPut(bon), dates_chantier: finalDates, equipements: updatedEquipements }
      );
      if (res.status === 200) {
        toast.success('Segment déplacé avec succès');
        fetchData();
      }
    } catch (e) {
      toast.error('Erreur lors du déplacement du segment');
    }
  };

  // Déplacer tout le bloc (garder la même durée, nouvelle date de début)
  // Puis proposer de décaler les suivants
  const handleMoveBlock = async (bon, newStartDate, skipConflictCheck = false) => {
    const oldDates = [...(bon.dates_chantier || [])].sort();
    if (oldDates.length === 0) return;
    const nbJours = oldDates.length;
    const oldStart = oldDates[0];
    if (newStartDate === oldStart) return;

    // Générer les nouvelles dates (même nb de jours ouvrables)
    const newDates = [];
    let current = new Date(newStartDate + 'T12:00:00');
    while (newDates.length < nbJours) {
      if (current.getDay() >= 1 && current.getDay() <= 5) {
        newDates.push(current.toISOString().split('T')[0]);
      }
      current.setDate(current.getDate() + 1);
    }

    const oldEnd = oldDates[oldDates.length - 1];
    const newEnd = newDates[newDates.length - 1];
    const daysDiff = Math.round((new Date(newEnd + 'T12:00:00') - new Date(oldEnd + 'T12:00:00')) / 86400000);

    // Sauvegarder les nouvelles dates pour le bloc déplacé
    const dayShift = Math.round((new Date(newDates[0] + 'T12:00:00') - new Date(oldDates[0] + 'T12:00:00')) / 86400000);
    const updatedEquipements = (bon.equipements || []).map(eq => ({
      ...eq,
      dates_utilisation: (eq.dates_utilisation || []).map(d => {
        const nd = new Date(new Date(d + 'T12:00:00').getTime() + dayShift * 86400000);
        while (nd.getDay() === 0 || nd.getDay() === 6) nd.setDate(nd.getDate() + (dayShift >= 0 ? 1 : -1));
        return nd.toISOString().split('T')[0];
      }).filter(d => newDates.includes(d))
    }));

    // ✅ POINT 4 : Détecter les conflits avec d'autres BTs du même chef
    // FIX: Ne détecter un conflit QUE si un AUTRE BT (pas le BT actuel) chevauche réellement
    if (!skipConflictCheck && bon.chef_equipe_id) {
      const conflictingBon = bons.find(b => {
        // Exclure le BT actuel
        if (b.id === bon.id) return false;
        
        // Même chef d'équipe
        if (b.chef_equipe_id !== bon.chef_equipe_id) return false;
        
        // Vérifier si les dates se chevauchent RÉELLEMENT
        const bDates = b.dates_chantier || [];
        const overlap = bDates.some(d => newDates.includes(d));
        
        return overlap;
      });
      
      // ✅ FIX: Vérifier que le conflit n'est pas un faux positif (données vides)
      if (conflictingBon && conflictingBon.client_nom_display && conflictingBon.numero_dossier) {
        // Conflit RÉEL détecté - demander à l'utilisateur quoi faire
        setShowConflitChoice({ 
          bon, 
          conflictingBon, 
          newDates, 
          updatedEquipements,
          dayShift 
        });
        return; // Stopper ici, attendre le choix de l'utilisateur
      }
    }

    // Mettre à jour CE bon immédiatement
    try {
      const res = await axios.put(`${API_URL}/api/crm/bons-travail/${bon.id}`,
        { ...cleanBonForPut(bon), dates_chantier: newDates, equipements: updatedEquipements },
        { headers: getAuthHeaders() }
      );
      if (res.status !== 200) { 
        const err = res.data || {}; 
        toast.error(err.detail || 'Erreur de mise a jour'); 
        return; 
      }
    } catch (e) { 
      const err = e.response?.data || {};
      toast.error(err.detail || 'Erreur reseau'); 
      return; 
    }

    // Proposer de décaler les suivants
    if (daysDiff !== 0) {
      setShowDecalage({ bon: { ...bon, dates_chantier: newDates }, newLastDate: newEnd, daysDiff, oldEnd, firstDate: newDates[0], moveMode: true });
    } else {
      toast.success(`Contrat deplace au ${newStartDate} (${nbJours} jours)`);
      fetchData();
    }
    // 🆕 Phase 3.5 — Check intra-jour après drop simple
    await checkIntraDayCollisionAfterDrop(bon, newDates);
  };

  // ✅ POINT 4 : Incruster le BT et décaler TOUS les suivants
  const handleIncrusterBT = async () => {
    if (!showConflitChoice) return;
    const { bon, newDates, updatedEquipements } = showConflitChoice;
    
    // 1. Sauvegarder le BT déplacé
    try {
      await axios.put(`${API_URL}/api/crm/bons-travail/${bon.id}`,
        { ...cleanBonForPut(bon), dates_chantier: newDates, equipements: updatedEquipements }
      );
    } catch (e) {
      toast.error('Erreur lors du déplacement');
      setShowConflitChoice(null);
      return;
    }
    
    // 2. Décaler TOUS les BTs suivants du même chef
    const chefBons = bons
      .filter(b => b.chef_equipe_id === bon.chef_equipe_id && b.id !== bon.id)
      .sort((a, b) => {
        const aStart = Math.min(...(a.dates_chantier || []).map(d => new Date(d + 'T12:00:00')));
        const bStart = Math.min(...(b.dates_chantier || []).map(d => new Date(d + 'T12:00:00')));
        return aStart - bStart;
      });
    
    const newStartDate = newDates[0];
    const newEndDate = newDates[newDates.length - 1];
    
    for (const otherBon of chefBons) {
      const otherDates = [...(otherBon.dates_chantier || [])].sort();
      if (otherDates.length === 0) continue;
      
      const otherStart = otherDates[0];
      
      // Si ce BT commence dans la plage du BT déplacé, le décaler
      if (otherStart >= newStartDate && otherStart <= newEndDate) {
        const nbJoursOther = otherDates.length;
        const newOtherStart = new Date(newEndDate + 'T12:00:00');
        newOtherStart.setDate(newOtherStart.getDate() + 1);
        while (newOtherStart.getDay() === 0 || newOtherStart.getDay() === 6) {
          newOtherStart.setDate(newOtherStart.getDate() + 1);
        }
        
        const newOtherDates = [];
        let current = new Date(newOtherStart);
        while (newOtherDates.length < nbJoursOther) {
          if (current.getDay() >= 1 && current.getDay() <= 5) {
            newOtherDates.push(current.toISOString().split('T')[0]);
          }
          current.setDate(current.getDate() + 1);
        }
        
        await axios.put(`${API_URL}/api/crm/bons-travail/${otherBon.id}`,
          { ...cleanBonForPut(otherBon), dates_chantier: newOtherDates }
        );
      }
    }
    
    toast.success('BT incrusté et suivants décalés !');
    setShowConflitChoice(null);
    fetchData();
    // 🆕 Phase 3.5 — Check intra-jour après incrustation
    await checkIntraDayCollisionAfterDrop(bon, newDates);
  };

  // ✅ POINT 4 : Interchanger les deux BTs
  const handleInterchangerBT = async () => {
    if (!showConflitChoice) return;
    const { bon, conflictingBon, newDates } = showConflitChoice;
    
    // Les dates du BT en conflit deviennent celles de l'ancien BT déplacé
    const oldDates = [...(bon.dates_chantier || [])].sort();
    
    try {
      // Sauvegarder le BT déplacé avec les nouvelles dates
      await axios.put(`${API_URL}/api/crm/bons-travail/${bon.id}`,
        { ...cleanBonForPut(bon), dates_chantier: newDates }
      );
      
      // Déplacer le BT en conflit vers les anciennes dates
      await axios.put(`${API_URL}/api/crm/bons-travail/${conflictingBon.id}`,
        { ...cleanBonForPut(conflictingBon), dates_chantier: oldDates }
      );
      
      toast.success('BTs interchangés avec succès !');
      setShowConflitChoice(null);
      fetchData();
      // 🆕 Phase 3.5 — Check intra-jour (le BT déplacé peut avoir d'autres BT du même chef sur ses nouvelles dates)
      await checkIntraDayCollisionAfterDrop(bon, newDates);
    } catch (e) {
      toast.error('Erreur lors de l\'interchange');
      setShowConflitChoice(null);
    }
  };

  const handleExtendBon = async (bon, newLastDate) => {
    const oldDates = [...(bon.dates_chantier || [])].sort();
    if (oldDates.length === 0) return;
    const oldEnd = oldDates[oldDates.length - 1];
    const firstDate = oldDates[0];
    if (newLastDate === oldEnd) return;
    // Don't allow shrinking before the start date
    if (newLastDate < firstDate) return;
    const daysDiff = Math.round((new Date(newLastDate + 'T12:00:00') - new Date(oldEnd + 'T12:00:00')) / 86400000);
    setShowDecalage({ bon, newLastDate, daysDiff, oldEnd, firstDate });
  };

  const applyDecalage = async (shiftFollowing) => {
    const { bon, newLastDate, daysDiff, oldEnd, firstDate, moveMode } = showDecalage;
    
    if (moveMode) {
      // Mode déplacement: le bon est déjà sauvegardé, juste décaler les suivants si demandé
      if (shiftFollowing && daysDiff !== 0) {
        const chefBons = bons.filter(b => b.chef_equipe_id === bon.chef_equipe_id && b.id !== bon.id);
        for (const otherBon of chefBons) {
          const otherDates = (otherBon.dates_chantier || []).sort();
          if (otherDates.length > 0 && otherDates[0] > oldEnd) {
            const shiftedDates = otherDates.map(d => {
              const dt = new Date(d + 'T12:00:00');
              dt.setDate(dt.getDate() + daysDiff);
              while (dt.getDay() === 0 || dt.getDay() === 6) dt.setDate(dt.getDate() + (daysDiff > 0 ? 1 : -1));
              return dt.toISOString().split('T')[0];
            });
            await axios.put(`${API_URL}/api/crm/bons-travail/${otherBon.id}`,
              { ...cleanBonForPut(otherBon), dates_chantier: shiftedDates },
              { headers: getAuthHeaders() }
            );
          }
        }
        toast.success('Contrat déplacé + suivants décalés!');
      } else {
        // ✅ FIX: "Ce contrat seulement" - Le bon a déjà été mis à jour par drag & drop, on refresh juste
        toast.success('Contrat déplacé!');
      }
      setShowDecalage(null);
      fetchData();
      return;
    }

    // Mode extension/raccourcissement (slider)
    const oldDates = [...(bon.dates_chantier || [])].sort();
    // ✅ Préserver la structure non-consécutive des dates (ne pas remplir les trous)
    const newDates = [...oldDates]; // Copier les dates existantes
    const oldEndDate = new Date(oldEnd + 'T12:00:00');
    const newEndDate = new Date(newLastDate + 'T12:00:00');
    
    if (newEndDate > oldEndDate) {
      // Extension: ajouter des jours ouvrables après la dernière date
      let current = new Date(oldEndDate);
      let workdaysToAdd = Math.round((newEndDate - oldEndDate) / 86400000);
      let added = 0;
      while (added < workdaysToAdd) {
        current.setDate(current.getDate() + 1);
        if (current.getDay() >= 1 && current.getDay() <= 5) {
          const dateStr = current.toISOString().split('T')[0];
          if (!newDates.includes(dateStr)) {
            newDates.push(dateStr);
            added++;
          }
        }
      }
    } else if (newEndDate < oldEndDate) {
      // Raccourcissement: retirer les dernières dates jusqu'à atteindre la nouvelle fin
      const sortedDates = [...oldDates].sort();
      const targetIdx = sortedDates.findIndex(d => d > newLastDate);
      if (targetIdx > 0) {
        newDates.splice(0, newDates.length, ...sortedDates.slice(0, targetIdx));
      }
    }
    
    // Si le bon a des équipements, afficher le dialogue pour les dates d'équipements
    if (bon.equipements && bon.equipements.length > 0) {
      // Préparer les équipements avec les nouvelles dates possibles
      const equipementsAvecDates = bon.equipements.map(eq => ({
        ...eq,
        // Garder les anciennes dates qui sont encore dans les nouvelles dates
        dates_utilisation: (eq.dates_utilisation || []).filter(d => newDates.includes(d))
      }));
      
      setShowDecalage(null);
      setShowEquipementsDates({
        bon,
        newDates,
        equipements: equipementsAvecDates,
        shiftFollowing,
        daysDiff,
        oldEnd
      });
      return;
    }
    
    // Pas d'équipements, on applique directement
    await applyChanges(bon, newDates, bon.equipements || [], shiftFollowing, daysDiff, oldEnd);
  };

  const applyChanges = async (bon, newDates, updatedEquipements, shiftFollowing, daysDiff, oldEnd) => {
    try {
      await axios.put(`${API_URL}/api/crm/bons-travail/${bon.id}`,
        { ...cleanBonForPut(bon), dates_chantier: newDates, equipements: updatedEquipements },
        { headers: getAuthHeaders() }
      );
      // Shift following contracts if requested (for both extend and shrink)
      if (shiftFollowing && daysDiff !== 0) {
        const chefBons = bons.filter(b => b.chef_equipe_id === bon.chef_equipe_id && b.id !== bon.id);
        for (const otherBon of chefBons) {
          const otherDates = (otherBon.dates_chantier || []).sort();
          if (otherDates.length > 0 && otherDates[0] > oldEnd) {
            const shiftedDates = otherDates.map(d => {
              const dt = new Date(d + 'T12:00:00');
              dt.setDate(dt.getDate() + daysDiff);
              // Skip weekends
              while (dt.getDay() === 0 || dt.getDay() === 6) {
                dt.setDate(dt.getDate() + (daysDiff > 0 ? 1 : -1));
              }
              return dt.toISOString().split('T')[0];
            });
            await axios.put(`${API_URL}/api/crm/bons-travail/${otherBon.id}`,
              { ...cleanBonForPut(otherBon), dates_chantier: shiftedDates },
              { headers: getAuthHeaders() }
            );
          }
        }
      }
      toast.success(shiftFollowing ? 'Contrats décalés!' : 'Dates mises à jour!');
      fetchData();
    } catch (e) { toast.error('Erreur mise à jour'); }
    finally { 
      setShowDecalage(null); 
      setShowEquipementsDates(null);
    }
  };

  const toggleEquipementDate = (eqIdx, dateStr) => {
    setShowEquipementsDates(prev => {
      const newEquipements = [...prev.equipements];
      const eq = { ...newEquipements[eqIdx] };
      const dates = eq.dates_utilisation || [];
      if (dates.includes(dateStr)) {
        eq.dates_utilisation = dates.filter(d => d !== dateStr);
      } else {
        eq.dates_utilisation = [...dates, dateStr].sort();
      }
      newEquipements[eqIdx] = eq;
      return { ...prev, equipements: newEquipements };
    });
  };

  const applyEquipementsDates = () => {
    const { bon, newDates, equipements, shiftFollowing, daysDiff, oldEnd } = showEquipementsDates;
    applyChanges(bon, newDates, equipements, shiftFollowing, daysDiff, oldEnd);
  };

  const weeks = getWeeks();
  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  // 🆕 [Phase 56.4a] Objet partagé via CalendarContext avec les sous-composants
  // (notamment <CalendarWeekRow> en 56.4b). Centralisé pour éviter prop drilling.
  //
  // 🚀 [Phase v13.50 — 2026-07-14] Enrichissement des BT avec shadows planification
  //   approximative (Sujet 4.D). Le calcul est mémoïsé sur `bons` pour éviter les
  //   recomputations à chaque render. Les shadows apparaissent avec opacity/border
  //   distincts + lane spéciale `__non_assigne__` pour les BT approx sans chef.
  const bonsEnrichis = useMemo(() => enrichirBtsAvecApprox(bons), [bons]);

  const calendarContextValue = {
    // State du parent (lectures)
    bons: bonsEnrichis, employees, currentDate, routesLavage,
    congesParDate, noJobWorkers, unorderedByDate,
    agendaEvents, meteo, punchesCache, dossierCache,
    // Flags d'affichage
    showMeteo, showConges, showAgenda,
    // State drag/hover
    dragging, hoveredBon,
    // Setters
    setHoveredBon, setDragging, setRdvPopup,
    setShowServiceCallPunch, setOrdreDialog, setShowNoJobPanel,
    // Handlers métier (définis dans le parent, NON déplacés)
    handleBarClick, handleDayDoubleClick, handleExtendBon, openEditDatesDialog,
    // Fetchers
    fetchDossierInfo, fetchPunches,
    // Helpers / navigation
    toDateStr, navigate, presentsParJour,
  };

  // 🆕 [Phase 56.5 — 2026-07-09] Handler pour toggle un bon dans la sélection route
  const toggleBonRouteSelection = (bonId) => {
    setSelectedBonsRoute(prev => {
      const next = new Set(prev);
      if (next.has(bonId)) next.delete(bonId);
      else next.add(bonId);
      return next;
    });
  };

  // 🆕 [Phase 56.5 — 2026-07-09] Handler pour changement date route lavage (fetch nacelles)
  const handleRouteDateChange = async (d) => {
    setRouteDate(d);
    if (d) {
      try {
        const nr = await axios.get(`${API_URL}/api/vehicules/nacelles-disponibles?date=${d}`);
        setNacellesDisponibles(nr.data?.disponibles || []);
        const needNacelle = Array.from(selectedBonsRoute).some(id => bonsLavage.find(b => b.id === id)?.nacelle_lavage_requise);
        setNacelleWarning(needNacelle && (nr.data?.disponibles || []).length === 0);
      } catch { setNacellesDisponibles([]); }
    }
  };

  if (loading) return <div className="p-6"><div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" /></div></div>;

  // 🆕 [Phase 56.5 — 2026-07-09] Handler pour réintégrer un employé retiré à son BT
  //     (extrait depuis le panel Sans job inline, aucun changement fonctionnel).
  const handleReintegrer = async (orphelin, dateStr) => {
    try {
      await axios.post(`${API_URL}/api/calendrier/assign-to-bt`, {
        bt_id: orphelin.bt_id,
        employee_id: orphelin.employee_id,
        date: dateStr,
      });
      toast.success(`${orphelin.employee_nom} réintégré au BT`);
      await fetchData();
    } catch (e) {
      toast.error("Erreur lors de la réintégration");
    }
  };

  // 🆕 [Phase 56.5 — 2026-07-09] Toggle expand d'un jour du panel Sans job
  const toggleNoJobDayExpand = (dateStr) => {
    setExpandedNoJobDays(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      return next;
    });
  };

  return (
    <div className="p-2 sm:p-4 flex flex-col" style={{ height: 'calc(100vh - 20px)' }}>
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Calendrier de Production</h1>
        </div>
        {/* 🆕 [Phase 56.2] Bandeau d'action extrait vers <CalendarActionBar/> */}
        <CalendarActionBar panels={{
          meteo: { active: showMeteo, onToggle: () => setShowMeteo(!showMeteo) },
          conges: { active: showConges, onToggle: () => setShowConges(!showConges) },
          agenda: { active: showAgenda, onToggle: () => setShowAgenda(!showAgenda) },
          sans_job: { active: showNoJobPanel, count: Object.values(noJobWorkers).reduce((s, v) => s + (v?.length || 0), 0), onToggle: () => setShowNoJobPanel(!showNoJobPanel) },
          booking: { active: showBookingPanel, count: bookingAlerts.length, onToggle: () => setShowBookingPanel(!showBookingPanel) },
          projets_pluie: { onOpen: () => { fetchBonsPluie(); setShowJoursPluie(true); } },
          route_lavage: { active: showRoutePanel, onToggle: () => { fetchBonsLavage(); setShowRoutePanel(!showRoutePanel); } },
          message_horaire: { onOpen: () => setShowMessageHoraire(true) },
        }} />
      </div>

      <div className="flex-1 border rounded-lg overflow-hidden flex flex-col">
        {/* 🆕 [Phase 56.5 — 2026-07-09] Month navigation extraite */}
        <CalendarMonthNav
          label={`${MOIS[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
          onPrev={prevMonth}
          onNext={nextMonth}
          orgBadge={{
            date: orgBtDate,
            refreshKey: orgBtRefresh,
            onClick: () => setOrgBtDialogOpen(true),
          }}
        />

        {/* Calendar grid */}
          <div className="flex-1 flex flex-col min-h-0 overflow-x-auto">
            {/* Header */}
            <div className="grid grid-cols-7 bg-blue-500 text-white text-center shrink-0 min-w-[350px]">
              {JOURS_HEADER.map((j, jIdx) => (
                <div key={jIdx} className="py-2.5 text-xs sm:text-sm font-semibold">{j}</div>
              ))}
            </div>

            {/* Weeks - stretch to fill */}
            <CalendarProvider value={calendarContextValue}>
            <div className="flex-1 flex flex-col min-w-[350px]">
            {/* 🆕 [Phase 56.4b] Rendu de chaque semaine extrait vers <CalendarWeekRow/> */}
            {weeks.map((week, weekIdx) => {
              const weekBons = getBonsForWeek(week);
              return <CalendarWeekRow key={weekIdx} week={week} weekIdx={weekIdx} weekBons={weekBons} />;
            })}
            </div>
            </CalendarProvider>
          </div>

          {/* 🆕 [Phase 56.1] Légende extraite vers <CalendarLegende/> */}
          <CalendarLegende bons={bons} employees={employees} showAgenda={showAgenda} />
      </div>

      {/* Dialog décalage */}
      {/* 🆕 [Phase 56.3] DialogDecalage extrait */}
      <DialogDecalage data={showDecalage} onClose={() => setShowDecalage(null)} onApply={(decalerSuivants) => applyDecalage(decalerSuivants)} />

      {/* 🆕 [Phase 56.5 — 2026-07-09] EquipementsDatesDialog extrait */}
      <EquipementsDatesDialog
        data={showEquipementsDates}
        onCancel={() => setShowEquipementsDates(null)}
        onToggleDate={(eqIdx, dateStr) => toggleEquipementDate(eqIdx, dateStr)}
        onConfirm={() => applyEquipementsDates()}
      />

      {/* 🆕 [Phase 56.5 — 2026-07-09] EditDatesDialog extrait */}
      <EditDatesDialog
        data={showEditDates}
        selectedDate={editNewEndDate}
        onSelectDate={setEditNewEndDate}
        onCancel={() => setShowEditDates(null)}
        onConfirm={() => confirmEditDates()}
      />

      {/* ✅ POINT 3 : Dialog choix de segment */}
      {/* 🆕 [Phase 56.3] DialogSegmentChoice extrait */}
      <DialogSegmentChoice
        data={showSegmentChoice}
        onClose={() => setShowSegmentChoice(null)}
        onSegmentSelect={(bon, segment) => { setShowSegmentChoice(null); setEditNewEndDate(new Date(segment[0] + 'T12:00:00')); setShowEditDates({ bon, segment }); }}
        onBlockSelect={(bon, allDates) => { setShowSegmentChoice(null); setEditNewEndDate(new Date(allDates[0] + 'T12:00:00')); setShowEditDates({ bon }); }}
      />

      {/* 🆕 [Phase 56.3] DialogConflitChoice extrait */}
      <DialogConflitChoice
        data={showConflitChoice}
        onClose={() => setShowConflitChoice(null)}
        onIncruster={handleIncrusterBT}
        onInterchanger={handleInterchangerBT}
      />

      {/* Dialog Punch Service Call */}
      {showServiceCallPunch && (
        <ServiceCallPunchDialog
          sc={showServiceCallPunch}
          employees={employees}
          onClose={() => setShowServiceCallPunch(null)}
          onDeleted={() => { setShowServiceCallPunch(null); fetchData(); }}
        />
      )}

      {/* 🆕 Phase 3.5 — Dialog ordonnancement intra-jour */}
      {ordreDialog && (
        <OrdonnerBTsDialog
          open={!!ordreDialog}
          onClose={() => setOrdreDialog(null)}
          chefId={ordreDialog.chefId}
          chefNom={ordreDialog.chefNom}
          date={ordreDialog.date}
          onConfirm={() => {
            setOrdreDialog(null);
            fetchData();
          }}
        />
      )}

      {/* 🆕 [Phase 56.5 — 2026-07-09] SansJobPanel extrait */}
      {showNoJobPanel && (
        <SansJobPanel
          noJobWorkers={noJobWorkers}
          expandedDays={expandedNoJobDays}
          onToggleExpand={toggleNoJobDayExpand}
          onClose={() => setShowNoJobPanel(false)}
          onDiag={openDiagnostic}
          onAffecter={openAffecterDialog}
          onConge={openCongeDialog}
          onReintegrer={handleReintegrer}
        />
      )}

      {/* 🆕 2026-05-09 — Panel Booking Alerts (BT cédulé ≠ recommandé)
          🆕 2026-05-14 — Header sticky + raisons explicites */}
      {/* 🆕 [Phase 56.5 — 2026-07-09] BookingAlertsPanel extrait */}
      {showBookingPanel && (
        <BookingAlertsPanel
          alerts={bookingAlerts}
          actionLoadingId={bookingActionLoading}
          onClose={() => setShowBookingPanel(false)}
          onAdjust={(alert, deltaDays) => applyBookingAdjustment(alert, deltaDays)}
          onOpenBT={(alert) => navigate(`/crm/projets/${alert.dossier_id}?tab=bon-travail&bonId=${alert.bon_id}`)}
        />
      )}

      {/* 🆕 [Phase 56.5 — 2026-07-09] PluieRallongementDialog extrait */}
      <PluieRallongementDialog
        data={pluieRallongementDialog}
        onCancel={() => setPluieRallongementDialog(null)}
        onConfirm={(cascade) => confirmPluieInsertion(cascade)}
      />
      {showJoursPluie && (
        <JoursPluieSheet
          open={showJoursPluie}
          onClose={() => setShowJoursPluie(false)}
          bonsPluie={bonsPluie}
          meteo={meteo}
          onRefresh={() => { fetchBonsPluie(); fetchData(); }}
          navigate={navigate}
          currentDate={currentDate}
          onPluieConflict={(payload) => setPluieRallongementDialog(payload)}
        />
      )}

      {/* 🆕 [Phase 56.5 — 2026-07-09] RouteLavagePanel extrait */}
      {showRoutePanel && (
        <RouteLavagePanel
          routeDate={routeDate}
          onRouteDateChange={handleRouteDateChange}
          routeEquipe={routeEquipe}
          onRouteEquipeChange={setRouteEquipe}
          bonsLavage={bonsLavage}
          selectedBons={selectedBonsRoute}
          onToggleBon={toggleBonRouteSelection}
          allEmployees={allEmployees}
          nacellesDisponibles={nacellesDisponibles}
          nacelleWarning={nacelleWarning}
          saving={savingRoute}
          onClose={() => setShowRoutePanel(false)}
          onSubmit={() => handleCreateRoute()}
        />
      )}

      {/* 🆕 [Phase 56.5 — 2026-07-09] InsertBTDialog extrait */}
      <InsertBTDialog
        data={showInsertBT}
        search={searchBT}
        onSearchChange={setSearchBT}
        loading={loadingInsert}
        bons={unscheduledBons}
        employees={employees}
        onCancel={() => setShowInsertBT(null)}
        onInsert={(bon, date) => handleInsertBT(bon, date)}
      />

      {/* 🆕 [Phase 56.3] DialogRdvVisiteEffectuee extrait */}
      <DialogRdvVisiteEffectuee
        data={rdvPopup}
        onClose={() => setRdvPopup(null)}
        onConfirm={async () => {
          try {
            const res = await axios.patch(`${API_URL}/api/crm/rendez-vous/${rdvPopup.id}/visite-effectuee`);
            if (res.status !== 200) throw new Error();
            toast.success('Visite marquée comme effectuée ! Dossier → "Soum. à faire".');
            setRdvPopup(null); loadData();
          } catch { toast.error('Erreur lors de la mise à jour'); }
        }}
      />

      {/* 🆕 [Phase 56.5 — 2026-07-09] AffecterEmployeDialog extrait */}
      <AffecterEmployeDialog
        data={affecterDialog}
        bts={affecterBts}
        chosenBtId={affecterChosenBt}
        onChoose={setAffecterChosenBt}
        loading={affecterLoading}
        onCancel={() => setAffecterDialog(null)}
        onSubmit={() => submitAffecter()}
      />

      {/* 🆕 [Phase 56.5 — 2026-07-09] CongeRapideDialog extrait */}
      <CongeRapideDialog
        data={congeDialog}
        typesLoading={congeTypesLoading}
        types={congeTypesDispo}
        chosenType={congeType}
        onChooseType={setCongeType}
        submitting={congeLoading}
        onCancel={() => setCongeDialog(null)}
        onSubmit={() => submitConge()}
      />
      {/* 🆕 2026-05-07 — Modale Message horaire pour Messenger */}
      <MessageHoraireDialog
        open={showMessageHoraire}
        onClose={() => setShowMessageHoraire(false)}
      />

      {/* 🆕 [Phase 51] Dialog Organisation des BT (par employé/jour) */}
      <OrganisationBTDialog
        open={orgBtDialogOpen}
        onClose={() => setOrgBtDialogOpen(false)}
        date={orgBtDate}
        onSaved={() => setOrgBtRefresh(k => k + 1)}
      />

      {/* 🆕 [Phase 56.5 — 2026-07-09] DiagnosticSansJobDialog extrait */}
      <DiagnosticSansJobDialog
        state={diagModal}
        onClose={() => setDiagModal(m => ({ ...m, open: false }))}
      />
    </div>
  );
}
